import moment from 'moment';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';

import { REGISTRATION_DATA } from 'constants/localstorage';
import { isClient } from 'constants/app';

import { OtpChannel } from 'typings/auth';
import EmailRegister from './EmailRegister';
import NotifyVerification from './NotifyVerification';
import PhoneError from './PhoneError';
import PhoneRegister from './PhoneRegister';
import Selection from './Selection';
import SuccessfulRegistration from './SuccessfulRegistration';
import VerifyEmail from './VerifyEmail';
import VerifyPhone from './VerifyPhone';

import rootSlice, { selectors, effects } from './slice';
import validationSlice from './EmailRegister/slice';
import { IPayloadRegister } from './types';
import { formTypes, registerTypes, LS_PHONE_REGISTER } from './constants';
import { removeData } from './utils';

import { CustomStyle, RegisterStyle } from './styled';
import SendOTPMethod from './SendOTPMethod';
import OtpLimit from './OtpLimit';

function Register(): JSX.Element {
  const dispatch = useDispatch();
  const router = useRouter();

  const registerState = useSelector(selectors);
  const { currentForm, email, phoneNumber, type } = registerState;

  useEffect(() => {
    if (isClient()) {
      const storedData = localStorage.getItem(REGISTRATION_DATA);
      if (storedData) {
        const data = JSON.parse(storedData);
        const dateNow = moment();
        const storedAt = moment(data.storedAt);
        const diff = dateNow.diff(storedAt, 'minutes');
        if (diff < 10) {
          dispatch(rootSlice.actions.storeAllData(data));
        } else {
          removeData();
        }
      }
    }

    return () => {
      dispatch(rootSlice.actions.changeForm({ target: formTypes.SELECTION }));
    };
  }, []);

  const handleChangeForm = (targetForm: string) => {
    dispatch(rootSlice.actions.changeForm({ target: targetForm }));
  };

  const handleRegisterUser = (phone: string, code: string) => {
    dispatch(
      rootSlice.actions.savePhoneNumber({
        phoneNumber: phone,
        code,
      }),
    );
    handleChangeForm(formTypes.SEND_OTP_METHOD);
  };

  const handleSendOTPSubmit = (channel: OtpChannel) => {
    dispatch(
      effects.verifyPhone({
        code: registerState.code as string,
        phoneNumber,
        channel,
      }),
    );
  };

  const handleVerifyEmailRegistration = (data: IPayloadRegister) => {
    dispatch(effects.verifyRegistration(data));
  };

  const handleContinueVerifyEmail = () => {
    handleChangeForm(formTypes.VERIFY_EMAIL);
  };

  const handleContinueVerifyPhone = () => {
    handleChangeForm(formTypes.VERIFY_PHONE);
  };

  const handleVerifyEmail = (code: string) => {
    dispatch(effects.validateEmailOtp({ code }));
  };

  const handleVerifyPhone = (otp: string) => {
    dispatch(effects.validatePhoneOtp({ otp }));
  };

  const handleResendCodeEmail = () => {
    dispatch(effects.resendEmailOtp());
  };

  const handleResendCodePhone = () => {
    handleChangeForm(formTypes.SEND_OTP_METHOD);
  };

  const handleChangeEmail = () => {
    handleChangeForm(formTypes.EMAIL);
  };

  const handleChangePhone = () => {
    handleChangeForm(formTypes.PHONE);
  };

  const handleResetState = () => {
    dispatch(validationSlice.actions.resetAllValidations());
    dispatch(rootSlice.actions.resetAllData());
  };

  const handleBackToLogin = () => {
    if (isClient()) localStorage.setItem(LS_PHONE_REGISTER, phoneNumber);

    router.push('/login');
    removeData();
  };

  const renderFormComponent = (
    component: React.ReactNode,
    includeCustomStyle = true,
  ): JSX.Element => (
    <>
      <RegisterStyle />
      {includeCustomStyle && <CustomStyle />}
      {component}
    </>
  );

  switch (currentForm) {
    case formTypes.EMAIL:
      return renderFormComponent(
        <EmailRegister onRegister={handleVerifyEmailRegistration} />,
        false,
      );

    case formTypes.NOTIFY_VERIFICATION_EMAIL:
      return renderFormComponent(
        <NotifyVerification
          type="email"
          recipient={email}
          onContinue={handleContinueVerifyEmail}
        />,
      );

    case formTypes.VERIFY_EMAIL:
      return renderFormComponent(
        <VerifyEmail
          onVerify={handleVerifyEmail}
          onChangeEmail={handleChangeEmail}
          onResend={handleResendCodeEmail}
          email={email}
        />,
      );

    case formTypes.PHONE:
      return renderFormComponent(
        <PhoneRegister onRegister={handleRegisterUser} />,
      );

    case formTypes.SEND_OTP_METHOD:
      return renderFormComponent(
        <SendOTPMethod phone={phoneNumber} onSubmit={handleSendOTPSubmit} />,
      );

    case formTypes.NOTIFY_VERIFICATION_PHONE:
      return renderFormComponent(
        <NotifyVerification
          type="phone"
          recipient={phoneNumber}
          onContinue={handleContinueVerifyPhone}
        />,
      );

    case formTypes.PHONE_ERROR:
      return renderFormComponent(
        <PhoneError
          onBackLogin={handleBackToLogin}
          onBackChangePhone={handleChangePhone}
        />,
        false,
      );

    case formTypes.OTP_LIMIT:
      return renderFormComponent(
        <OtpLimit onBackClick={handleChangePhone} />,
        false,
      );

    case formTypes.VERIFY_PHONE:
      return renderFormComponent(
        <VerifyPhone
          onVerify={handleVerifyPhone}
          onChangePhone={handleChangePhone}
          onResend={handleResendCodePhone}
          phoneNumber={phoneNumber}
          autoRedirect={
            type !== registerTypes.GOOGLE && type !== registerTypes.FACEBOOK
          }
        />,
      );

    case formTypes.SUCCESS:
      return renderFormComponent(<SuccessfulRegistration />, false);

    case formTypes.SELECTION:
    default:
      return renderFormComponent(
        <Selection
          handleOnMount={handleResetState}
          handleNavigate={handleChangeEmail}
        />,
        false,
      );
  }
}

export default Register;
